import { Injectable } from '@angular/core';
import type { ExclusivePriceCell } from '@models/exclusive-prices';
import { AbstractPriceGridStore, gridKey, roundPrice } from '../../shared/pricing-grid/pricing-grid.store';
import type { ExclusiveCoords } from './exclusive-pricing.model';

/**
 * Owns the in-memory cell map for {@link ExclusivePricingComponent}: the whole season × stay-type grid of
 * one price list. Every mutation is memory-only until the section's Save runs the PUT. Provided at the
 * component level so each section gets isolated state.
 *
 * All cell state and math live in {@link AbstractPriceGridStore}, shared with the combination grids; what
 * is specific here is only that a cell is keyed by (season, stay type) — a whole-lake price does not
 * depend on the number of anglers — and the DTO shape.
 */
@Injectable()
export class ExclusivePricingGridStore extends AbstractPriceGridStore<ExclusiveCoords, ExclusivePriceCell> {
  load(cells: ExclusivePriceCell[]): void {
    this.replaceCells(
      cells.map(
        (c) =>
          [
            gridKey([c.periodTypeId, c.periodTimeId]),
            { purchasePrice: c.purchasePrice, sellingPrice: c.sellingPrice },
          ] as const,
      ),
    );
  }

  /**
   * Flatten to the save payload, rounded to the two decimals the column stores. Not filtered by the
   * water's current seasons/stay types — like the combination grids, cells for dimensions the water no
   * longer offers are sent back and dropped by the API.
   */
  toPayload(): ExclusivePriceCell[] {
    return this.filledCells().map(({ coords: [periodTypeId, periodTimeId], purchasePrice, sellingPrice }) => ({
      periodTypeId,
      periodTimeId,
      purchasePrice: roundPrice(purchasePrice),
      sellingPrice: roundPrice(sellingPrice),
    }));
  }
}
